// Money crosses the wire as a decimal string (Python Decimal -> JSON string).
// Number() is exact for V1 magnitudes (well below 2^53 cents); refactor to
// BigInt + manual locale composition if amounts ever grow past that bound.

const DECIMAL_STRING = /^-?\d+(\.\d+)?$/;

export interface MoneyFormatOptions {
  currency?: string;
  locale?: string;
}

export function formatMoney(value: string, options: MoneyFormatOptions = {}): string {
  const { currency = "USD", locale = "en-US" } = options;
  if (!DECIMAL_STRING.test(value)) {
    throw new Error(`formatMoney: invalid decimal string "${value}"`);
  }
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

// Like formatMoney but prefixes an explicit "+" for positive amounts (negative
// values already carry their "-"). Used by the colored KPI tiles where the sign
// reinforces the green/red tone (e.g. "+$125.00" / "-$40.00"). Zero renders
// without a sign.
export function formatSignedMoney(
  value: string,
  options: MoneyFormatOptions = {},
): string {
  const formatted = formatMoney(value, options);
  return Number(value) > 0 ? `+${formatted}` : formatted;
}

// Per-share / per-contract price formatter shared by the Trades-grid Entry /
// Exit columns, the trade-detail Executions table, and the What-If page.
// Unlike formatMoney (fixed 2dp for P&L), prices use extra precision only for
// sub-dollar instruments: a price whose magnitude is below $1 keeps up to 4
// fraction digits (e.g. 0.881 -> $0.881) so penny-stock / forex ticks survive,
// while a price of $1 or more renders at the conventional 2dp (1.2345 -> $1.23).
export function formatPrice(value: string, options: MoneyFormatOptions = {}): string {
  const { currency = "USD", locale = "en-US" } = options;
  if (!DECIMAL_STRING.test(value)) {
    throw new Error(`formatPrice: invalid decimal string "${value}"`);
  }
  const maximumFractionDigits = Math.abs(Number(value)) < 1 ? 4 : 2;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits,
  }).format(Number(value));
}

// --- Exact decimal-string arithmetic ---------------------------------------
// Money and share quantities both cross the wire as decimal strings (Python
// Decimal -> JSON). Summing or equality-testing them via Number()/parseFloat
// reintroduces binary-float drift (0.1 + 0.2 !== 0.3), which surfaced as wrong
// calendar period totals and a "flat" position that never reads flat with
// fractional shares. These helpers parse each string to a scaled BigInt at the
// maximum fractional scale present across the inputs, operate on integers, and
// render back to a decimal string -- exact regardless of scale, no hardcoded
// 2dp assumption.

function fractionalScale(value: string): number {
  const dot = value.indexOf(".");
  return dot === -1 ? 0 : value.length - dot - 1;
}

// Parse a validated decimal string to a signed BigInt of minor units at the
// given scale (e.g. "1.20" at scale 3 -> 1200n). The DECIMAL_STRING guard
// upstream guarantees at least one integer digit, so the assembled digit run is
// never empty.
function toScaledInt(value: string, scale: number): bigint {
  const negative = value.startsWith("-");
  const unsigned = negative ? value.slice(1) : value;
  const dot = unsigned.indexOf(".");
  const intPart = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const fracPart = dot === -1 ? "" : unsigned.slice(dot + 1);
  const magnitude = BigInt(`${intPart}${fracPart.padEnd(scale, "0")}`);
  return negative ? -magnitude : magnitude;
}

function fromScaledInt(total: bigint, scale: number): string {
  if (scale === 0) return total.toString();
  const negative = total < 0n;
  const digits = (negative ? -total : total).toString().padStart(scale + 1, "0");
  const cut = digits.length - scale;
  const body = `${digits.slice(0, cut)}.${digits.slice(cut)}`;
  return negative ? `-${body}` : body;
}

// Exact sum of decimal-string amounts. The result carries the widest fractional
// scale present in the inputs (an empty list sums to "0").
export function sumDecimals(values: ReadonlyArray<string>): string {
  let scale = 0;
  for (const value of values) {
    if (!DECIMAL_STRING.test(value)) {
      throw new Error(`sumDecimals: invalid decimal string "${value}"`);
    }
    scale = Math.max(scale, fractionalScale(value));
  }
  let total = 0n;
  for (const value of values) total += toScaledInt(value, scale);
  return fromScaledInt(total, scale);
}

// Exact zero test for a decimal string -- true for "0", "-0", "0.00" alike.
// Use in place of `Number(value) === 0` where float drift would misclassify a
// running position (e.g. a "flat" check across fractional-share fills).
export function isZeroDecimal(value: string): boolean {
  if (!DECIMAL_STRING.test(value)) {
    throw new Error(`isZeroDecimal: invalid decimal string "${value}"`);
  }
  return toScaledInt(value, fractionalScale(value)) === 0n;
}
